import { useState, useCallback, useMemo, useEffect } from 'react';
import { VESSEL_DISPLAY_NONE } from '../utils/vesselUtils';

/** Persisted preference: dots vs demo PNG icons for exercise / fleet markers (only applied when Exercise scenario map layer is on). */
const VESSEL_MARKER_DISPLAY_STORAGE_KEY = 'bluetide-vessel-marker-display';

function readStoredVesselMarkerDisplay() {
  if (typeof window === 'undefined') return 'dots';
  try {
    const v = window.localStorage.getItem(VESSEL_MARKER_DISPLAY_STORAGE_KEY);
    if (v === 'icons' || v === 'dots') return v;
  } catch (_) {
    /* ignore */
  }
  return 'dots';
}

/**
 * Hook for managing UI control selections and validation
 * @param {Array} availableModels - Available model options
 * @param {Array} availableDepths - Available depth options
 *param {Array} availableDates - Available date options
 * @param {Array} availableTimes - Available time options
 * @returns {object} UI controls state and functions
 */
export const useUIControls = (
  availableModels = [],
  availableDepths = [],
  availableDates = [],
  availableTimes = []
) => {
  // --- Core UI State ---
  const [selectedArea, setSelectedArea] = useState('surface_vessel_data');  // Default to first BlueTide datasource
  const [selectedModel, setSelectedModel] = useState(null);  // No model for BlueTide datasources
  const [selectedDepth, setSelectedDepth] = useState(0);
  const [currentDate, setCurrentDate] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [selectedStation, setSelectedStation] = useState(null);
  const [activeParameter, setActiveParameter] = useState('oceanCurrents'); // Tracks the last active layer

  // --- Map Layer Visibility State ---
  const [mapLayerVisibility, setMapLayerVisibility] = useState({
    // Off by default so user can explicitly toggle them on
    oceanCurrents: false,
    temperature: false,
    stations: true,
    track: false, // User can enable full vessel track path
    latestVessels: true, // Latest vessel positions layer (default on)
    velocityArrows: true, // Direction/heading arrows on vessel markers (Step 5)
    threatZones: false, // Threat zone overlay in Vessel Tracking (Step 5)
    currentSpeed: false,
    currentDirection: false,
    ssh: false,
    waveDirection: false,
    salinity: false,
    pressure: false,
    windSpeed: false,
    windDirection: false,
    windVelocity: false,
    vesselTrail: true,
    threats: false,
    /** Narragansett-area exercise: Op Alpha, threat polygon, CBU / loiter / convergence, comms lines (default on) */
    exerciseScenario: true,
  });

  // --- View Mode State ---
  // Keep default label as-is so it can be animated later
  const [viewMode, setViewMode] = useState('Vessel Tracking'); // Options: 'Ocean Analysis', 'Vessel Tracking', 'Marker Clusters', 'Threat Zone', 'Relative Grid View'

  // --- Selected vessel for map display (VESSEL_DISPLAY_NONE = none, null = all vessels, string = one vessel) ---
  const [selectedVesselId, setSelectedVesselId] = useState(VESSEL_DISPLAY_NONE);

  // --- Vessel filters (Step 5): threat level and vessel type ---
  // Default to 'threat' so only vessels with threat level above 0 are shown (reduces noise; Vincent feedback)
  const [threatLevelFilter, setThreatLevelFilter] = useState('threat'); // 'all' | 'no-threat' | 'threat'
  // Step 5: multi-select vessel types; empty array = all types, else show only vessels whose type is in the array
  const [vesselTypeFilter, setVesselTypeFilter] = useState([]); // string[] (e.g. ['Cargo', 'Cruise'])

  /** 'dots' = deck.gl dot/chevron clusters; 'icons' = vessel icon atlas in MarkerClusterLayer */
  const [vesselMarkerDisplay, setVesselMarkerDisplayState] = useState(readStoredVesselMarkerDisplay);

  useEffect(() => {
    try {
      window.localStorage.setItem(VESSEL_MARKER_DISPLAY_STORAGE_KEY, vesselMarkerDisplay);
    } catch (_) {
      /* ignore */
    }
  }, [vesselMarkerDisplay]);

  const setVesselMarkerDisplay = useCallback((next) => {
    if (next === 'icons' || next === 'dots') setVesselMarkerDisplayState(next);
  }, []);

  // --- Heatmap Scale State ---
  const [heatmapScale, setHeatmapScale] = useState(1); // Default scale is 1x

  // --- Wind Velocity Particle Configuration ---
  const [windVelocityParticleCount, setWindVelocityParticleCount] = useState(2000);
  const [windVelocityParticleOpacity, setWindVelocityParticleOpacity] = useState(0.9);
  const [windVelocityParticleSpeed, setWindVelocityParticleSpeed] = useState(1.2);

  // --- Layer Animation State (for particle/vector layers) ---
  // Defaults to false (paused) for oceanCurrents and windVelocity
  const [layerAnimationEnabled, setLayerAnimationEnabled] = useState({
    oceanCurrents: false,
    windVelocity: false,
    threats: true,
  });

  // --- UI Configuration ---
  const [uiConfig, setUiConfig] = useState({
    autoSelectDefaults: true,
    validateSelections: true,
    persistSelections: false
  });

  // Heatmap visibility is now automatically controlled by temperature layer
  const isSstHeatmapVisible = mapLayerVisibility.temperature;

  // Toggle function for the Heatmap (now a no-op since heatmap is automatic)
  const toggleSstHeatmap = useCallback(() => {
    // No-op: heatmap is now automatically controlled by temperature layer
  }, []);

  // Toggle function for primary map layers
  const toggleMapLayer = useCallback((layerName) => {
    setMapLayerVisibility(prev => {
      const isTurningOn = !prev[layerName];
      if (isTurningOn) {
        setActiveParameter(layerName);
      }
      return {
        ...prev,
        [layerName]: !prev[layerName],
      };
    });
  }, []);

  // Toggle function for layer animation (particle/vector layers)
  const toggleLayerAnimation = useCallback((layerName) => {
    setLayerAnimationEnabled(prev => ({
      ...prev,
      [layerName]: !prev[layerName],
    }));
  }, []);

  // --- Available Options ---
  // Note: Legacy area options (MBL, MSR, USM) removed - now using dynamic BlueTide datasources
  const availableAreas = useMemo(() => [], []);

  // Note: Legacy ocean models (NGOFS2) removed - BlueTide datasources don't use models
  const defaultOceanModels = useMemo(() => [], []);

  // --- Validated setters ---
  const setSelectedAreaValidated = useCallback((area) => {
    setSelectedArea(area);
  }, []);

  const setSelectedModelValidated = useCallback((model) => {
    const modelsToCheck = availableModels.length > 0 ? availableModels : defaultOceanModels;
    if (modelsToCheck.includes(model) || !uiConfig.validateSelections) {
      setSelectedModel(model);
    }
  }, [availableModels, defaultOceanModels, uiConfig.validateSelections]);

  const setSelectedDepthValidated = useCallback((depth) => {
    if (availableDepths.includes(depth) || depth === null || !uiConfig.validateSelections) {
      setSelectedDepth(depth);
    }
  }, [availableDepths, uiConfig.validateSelections]);

  const setSelectedDateValidated = useCallback((date) => {
    if (date === '' || availableDates.includes(date) || !uiConfig.validateSelections) {
      setCurrentDate(date);
    }
  }, [availableDates, uiConfig.validateSelections]);

  const setSelectedTimeValidated = useCallback((time) => {
    if (time === '' || availableTimes.includes(time) || !uiConfig.validateSelections) {
      setCurrentTime(time);
    }
  }, [availableTimes, uiConfig.validateSelections]);

  const setViewModeHandler = useCallback((mode) => {
    setViewMode(mode);
    setMapLayerVisibility(prev => {
      if (mode === 'Vessel Tracking') {
        // Default for Vessel Tracking: latest positions on, full track off, arrows on, threat zones off
        return {
          ...prev,
          track: false,
          latestVessels: true,
          velocityArrows: prev.velocityArrows !== undefined ? prev.velocityArrows : true,
          threatZones: prev.threatZones !== undefined ? prev.threatZones : false,
        };
      }
      return prev;
    });
  }, []);


  // --- Selection validation ---
  const selectionValidation = useMemo(() => {
    const errors = [];
    const warnings = [];
    const modelsToCheck = availableModels.length > 0 ? availableModels : defaultOceanModels;

    if (!availableAreas.find(a => a.value === selectedArea)) {
      warnings.push(`Selected area "${selectedArea}" may not be available`);
    }

    if (modelsToCheck.length > 0 && !modelsToCheck.includes(selectedModel)) {
      errors.push(`Selected model "${selectedModel}" is not available`);
    }

    if (availableDepths.length > 0 && selectedDepth !== null && !availableDepths.includes(selectedDepth)) {
      errors.push(`Selected depth "${selectedDepth}ft" is not available`);
    }

    if (availableDates.length > 0 && currentDate && !availableDates.includes(currentDate)) {
      errors.push(`Selected date "${currentDate}" is not available`);
    }

    if (availableTimes.length > 0 && currentTime && !availableTimes.includes(currentTime)) {
      errors.push(`Selected time "${currentTime}" is not available`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }, [
    selectedArea, selectedModel, selectedDepth, currentDate, currentTime,
    availableAreas, availableModels, defaultOceanModels, availableDepths, availableDates, availableTimes
  ]);

  // --- Current selections info ---
  const currentSelections = useMemo(() => {
    const areaInfo = availableAreas.find(a => a.value === selectedArea);
    const modelsToCheck = availableModels.length > 0 ? availableModels : defaultOceanModels;

    return {
      area: {
        value: selectedArea,
        label: areaInfo?.label || selectedArea,
        region: areaInfo?.region || 'Unknown'
      },
      model: {
        value: selectedModel,
        available: modelsToCheck.includes(selectedModel)
      },
      depth: {
        value: selectedDepth,
        available: selectedDepth === null || availableDepths.includes(selectedDepth),
        unit: 'ft'
      },
      parameter: {
        value: activeParameter, // Reflects the last active layer
        label: activeParameter,
        category: 'Unknown'
      },
      date: {
        value: currentDate,
        available: !currentDate || availableDates.includes(currentDate)
      },
      time: {
        value: currentTime,
        available: !currentTime || availableTimes.includes(currentTime)
      },
      station: selectedStation
    };
  }, [
    selectedArea, selectedModel, selectedDepth, activeParameter, currentDate, currentTime, selectedStation,
    availableAreas, availableModels, defaultOceanModels, availableDepths, availableDates, availableTimes
  ]);

  // --- Bulk selection update ---
  const updateSelections = useCallback((selections) => {
    if (selections.area !== undefined) setSelectedAreaValidated(selections.area);
    if (selections.model !== undefined) setSelectedModelValidated(selections.model);
    if (selections.depth !== undefined) setSelectedDepthValidated(selections.depth);
    if (selections.parameter !== undefined) setActiveParameter(selections.parameter); // Kept for potential external control
    if (selections.date !== undefined) setSelectedDateValidated(selections.date);
    if (selections.time !== undefined) setSelectedTimeValidated(selections.time);
    if (selections.station !== undefined) setSelectedStation(selections.station);
  }, [
    setSelectedAreaValidated, setSelectedModelValidated, setSelectedDepthValidated,
    setSelectedDateValidated, setSelectedTimeValidated
  ]);

  // --- Reset to defaults ---
  const resetToDefaults = useCallback(() => {
    setSelectedArea('surface_vessel_data');  // BlueTide default datasource
    setSelectedModel(null);  // No model for BlueTide
    setSelectedDepth(availableDepths[0] || null);
    setActiveParameter('oceanCurrents');
    setCurrentDate(availableDates[0] || '');
    setCurrentTime(availableTimes[0] || '');
    setSelectedStation(null);
  }, [availableDepths, availableDates, availableTimes]);

  // --- Update configuration ---
  const updateUiConfig = useCallback((newConfig) => {
    setUiConfig(prev => ({ ...prev, ...newConfig }));
  }, []);

  // --- Export current configuration ---
  const exportConfiguration = useCallback(() => {
    const modelsToExport = availableModels.length > 0 ? availableModels : defaultOceanModels;
    return {
      timestamp: new Date().toISOString(),
      selections: {
        area: selectedArea,
        model: selectedModel,
        depth: selectedDepth,
        activeParameter: activeParameter,
        date: currentDate,
        time: currentTime,
        station: selectedStation?.name || null
      },
      validation: selectionValidation,
      availableOptions: {
        models: modelsToExport,
        depths: availableDepths,
        areas: availableAreas.map(a => a.value),
        dates: availableDates,
        times: availableTimes
      }
    };
  }, [
    selectedArea, selectedModel, selectedDepth, activeParameter, currentDate, currentTime, selectedStation,
    selectionValidation, availableModels, defaultOceanModels, availableDepths, availableAreas,
    availableDates, availableTimes
  ]);

  // --- Quick selection shortcuts ---
  const selectNextDepth = useCallback(() => {
    if (availableDepths.length === 0) return;
    const currentIndex = availableDepths.indexOf(selectedDepth);
    const nextIndex = (currentIndex + 1) % availableDepths.length;
    setSelectedDepthValidated(availableDepths[nextIndex]);
  }, [availableDepths, selectedDepth, setSelectedDepthValidated]);

  const selectPreviousDepth = useCallback(() => {
    if (availableDepths.length === 0) return;
    const currentIndex = availableDepths.indexOf(selectedDepth);
    const prevIndex = currentIndex <= 0 ? availableDepths.length - 1 : currentIndex - 1;
    setSelectedDepthValidated(availableDepths[prevIndex]);
  }, [availableDepths, selectedDepth, setSelectedDepthValidated]);

  const selectNextModel = useCallback(() => {
    const modelsToUse = availableModels.length > 0 ? availableModels : defaultOceanModels;
    if (modelsToUse.length === 0) return;
    const currentIndex = modelsToUse.indexOf(selectedModel);
    const nextIndex = (currentIndex + 1) % modelsToUse.length;
    setSelectedModelValidated(modelsToUse[nextIndex]);
  }, [availableModels, defaultOceanModels, selectedModel, setSelectedModelValidated]);

  // --- Return public API ---
  return {
    // Current selections
    selectedArea,
    selectedModel,
    selectedDepth,
    selectedParameter: activeParameter, // Keep name for compatibility
    currentDate,
    currentTime,
    selectedStation,
    viewMode,
    vesselMarkerDisplay,
    setVesselMarkerDisplay,

    // Layer visibility
    mapLayerVisibility,
    isSstHeatmapVisible,

    // Heatmap Scale
    heatmapScale,
    setHeatmapScale,

    // Wind Velocity Particle Configuration
    windVelocityParticleCount,
    setWindVelocityParticleCount,
    windVelocityParticleOpacity,
    setWindVelocityParticleOpacity,
    windVelocityParticleSpeed,
    setWindVelocityParticleSpeed,

    // Setters (validated)
    setSelectedArea: setSelectedAreaValidated,
    setSelectedModel: setSelectedModelValidated,
    setSelectedDepth: setSelectedDepth,
    setSelectedParameter: setActiveParameter, // Keep name for compatibility
    setSelectedDate: setSelectedDateValidated,
    setSelectedTime: setSelectedTimeValidated,
    setSelectedStation,
    setViewMode: setViewModeHandler,
    selectedVesselId,
    setSelectedVesselId,
    threatLevelFilter,
    setThreatLevelFilter,
    vesselTypeFilter,
    setVesselTypeFilter,

    // Layer toggles
    toggleSstHeatmap,
    toggleMapLayer,

    // Layer animation controls
    layerAnimationEnabled,
    toggleLayerAnimation,

    // Available options
    availableAreas,
    defaultOceanModels,

    // Configuration
    uiConfig,
    updateUiConfig,

    // Bulk operations
    updateSelections,
    resetToDefaults,

    // Navigation shortcuts
    selectNextDepth,
    selectPreviousDepth,
    selectNextModel,

    // Validation and info
    selectionValidation,
    currentSelections,

    // Export
    exportConfiguration,

    // Computed values
    hasValidSelections: selectionValidation.isValid,
    selectedAreaInfo: currentSelections.area,
    selectedParameterInfo: currentSelections.parameter,
    isDepthSelected: selectedDepth !== null,
    isStationSelected: selectedStation !== null
  };
};